import {
	StepAccordion,
	AccordionMinimumHeight,
	ButtonColorEnum,
	AccordionBackgroundColor,
} from "../../../../Utils/StepAccordion";
import { InputComponent } from "../../../../Utils/UI/InputComponent/InputComponent";
import IStepInterface from "../../../../../Interfaces/IStepInterface";
import { useContext, useMemo, useState } from "react";
import PartnerContentContext from "../../../../../Contexts/PartnerContentContext";
import { Autocomplete, Box, styled, TextField } from "@mui/material";
import { StoreChain } from "../../../../../Models/PartnerContentModel";
import { useOrder } from "../../../../../Contexts/OrderContext";

interface IOption {
	label: string;
	id: number;
	name: string;
	installationNumber: number;
}

export const SalesStep = ({
	nextStep,
	previousStep,
	open,
	toggleOpen,
}: IStepInterface) => {
	const { order, updateOrder } = useOrder();
	const [salesPersonError, setSalesPersonError] = useState<boolean>(false);
	const partnerContentContext = useContext(PartnerContentContext);

	function salesPersonValidator(input: string): boolean {
		if (input.length === 0) return true;
		return /^\S+[a-zA-ZæÆøØåÅ\s\d]+$/.test(input);
	}

	function handleSalesIdChange(result: { value: string; valid: boolean; error: string }): any {
		setSalesPersonError(!result.valid);
		updateOrder({ salesPersonId: result.value });
	}

	function handleShopIdChange(text: { value: string; valid: boolean; error: string }): any {
		updateOrder({ shopId: text.value });
	}

	function handleStoreChainChange(value: IOption | null) {
		if (value) {
			updateOrder({
				shopInstallationNumber: value.installationNumber,
				shopId: value.id.toString(),
				shopName: value.name,
			});
		} else {
			updateOrder({
				shopInstallationNumber: undefined,
				shopId: "",
				shopName: "",
			});
		}
	}

	function getStoreChainOptions(storeChains: Array<StoreChain>): Array<IOption> {
		const options: Array<IOption> = storeChains.map((storeChain) => {
			if (storeChain.installationNumber === undefined)
				storeChain.installationNumber = Number(partnerContentContext.partnerInstallationNumber);
			return {
				label: `${storeChain.name}${
					storeChain.installationNumber !== undefined ? " - " + storeChain.installationNumber : ""
				}${storeChain.storeNumber > 0 ? " - " + storeChain.storeNumber : ""} `,
				id: storeChain.storeNumber,
				name: storeChain.name,
				installationNumber: storeChain.installationNumber,
			};
		});
		return options;
	}

	const formValid = useMemo(() => {
		if (order.salesPersonId.length === 0 || salesPersonError) return false;
		const onlyDigitsInShopId = /^\d+$/.test(order.shopId);
		if (!onlyDigitsInShopId) return false;
		if (order.chosenProducts.chargingstandProduct !== undefined) return true;
		if (partnerContentContext.isStoreChain && order.shopInstallationNumber === undefined)
			return false;
		return true;
	}, [
		order.chosenProducts,
		order.shopId,
		order.shopInstallationNumber,
		salesPersonError,
		order.salesPersonId,
	]);

	return (
		<StepAccordion
			loading={false}
			loadingText={partnerContentContext.stepLoader.text}
			loadingTitle={partnerContentContext.stepLoader.title}
			label={partnerContentContext.salesInformationStep?.title || "Sælgeroplysninger"}
			open={open}
			click={toggleOpen}
			firstButtonLabel={partnerContentContext.salesInformationStep?.backButton || "Tilbage"}
			firstButtonOutline={true}
			firstButton={previousStep}
			secondButtonLabel={partnerContentContext.salesInformationStep?.nextButton || "Næste"}
			secondButtonType={ButtonColorEnum.DARK_GREEN}
			secondButton={nextStep}
			secondButtonDisabled={!formValid}
			minimumHeight={AccordionMinimumHeight.SMALL}
			firstButtonId="SalesStepReturn"
			secondButtonId="SalesStepForward"
			secondButtonShadow={true}
			backgroundColor={AccordionBackgroundColor.GREY}
		>
			<Box>
				<Box display={"flex"}>
					<Box flex={1}>
						<InputComponent
							validationType="none"
							value={order.salesPersonId}
							placeholder="Sælger ID"
							change={handleSalesIdChange}
							customValidator={salesPersonValidator}
							customTextValidationError="Må ikke indeholde specialtegn."
							inputProps={{ maxLength: 40 }}
						/>
					</Box>
					
					{partnerContentContext.isStoreChain ? (
						<Box flex={1} ml={"10px"}>
							<StyledAutocomplete
								size="medium"
								defaultValue={{ label: "Vælg afdeling", id: -1, name: "", installationNumber: 0 }}
								disablePortal
								onChange={(e, value) => {
									handleStoreChainChange(value);
								}}
								options={getStoreChainOptions(partnerContentContext.storeChains)}
								fullWidth={true}
								renderInput={(params) => <TextField {...params} />}
							/>
						</Box>
					) : (
						<Box flex={1} ml={"10px"}>
							<InputComponent
								value={order.shopId}
								placeholder={
									partnerContentContext.salesInformationStep?.shopIdPlaceholder || "Butiksnummer"
								}
								validationType="number"
								errorMessage="Må kun indholde tal"
								change={handleShopIdChange}
							/>
						</Box>
					)}
				</Box>
			</Box>
		</StepAccordion>
	);
};

const StyledAutocomplete = styled(Autocomplete)({
	"& .MuiOutlinedInput-root": {
		fontFamily: '"NORLYSText-Regular"',
		fontWeight: 300,
		backgroundColor: "#fff",
		padding: "14px 24px !important",
		minHeight: 56,
		borderRadius: 6,
		display: "flex",
		alignItems: "center",
		"& .MuiOutlinedInput-input": {
			padding: "0 !important",
			fontFamily: '"NORLYSText-Regular"',
			fontWeight: 300,
			lineHeight: 2,
		},
		"& .MuiOutlinedInput-notchedOutline": {
			top: "0 !important",
			border: "2px solid #d6d6d6",
			borderRadius: 6,
			transition: "border-color 0.2s ease",
		},
		"&:hover .MuiOutlinedInput-notchedOutline": {
			borderColor: "#d6d6d6",
		},
		"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderColor: "#25d287",
			borderWidth: 2,
		},
	},
	"& .MuiOutlinedInput-notchedOutline legend": {
		display: "none",
	},
	"& .MuiInputLabel-root": {
		fontFamily: '"NORLYSText-Regular"',
		fontWeight: 300,
		color: "#838383",
		transform: "translate(24px, 16px) scale(1)",
		"&.Mui-focused, &.MuiFormLabel-filled": {
			transform: "translate(26px, -5px) scale(0.6)",
			transformOrigin: "left bottom",
			backgroundColor: "#fff",
			padding: "0 4px",
		},
		"&.Mui-focused": {
			color: "#838383",
		},
	},
}) as typeof Autocomplete;
